import { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { useMutation } from '@tanstack/react-query';
import { publicApi } from '@api/client';
import { customerDetailsSchema, type CustomerDetailsFormValues } from '@api/schemas';
import { FormField } from '@components/common/FormField';
import { Button } from '@components/ui/button';
import type { WidgetState } from './widgetState';
import type { CreateBookingResponse } from '@api/types';
import { ApiRequestError } from '@api/client';

interface DetailsStepProps {
  widgetState: WidgetState;
  centerSlug: string;
  /** IANA timezone of the centre, used to render the confirmation summary. */
  centerTimezone: string;
  onConfirmed: (
    id: string,
    trackingToken: string,
    confirmationDeadline: string | null,
  ) => void;
  onSlotTaken: () => void;
  onBack: () => void;
}

// Key de prod (app.equipomag.com). El build de prod (`npm run build:frontend`)
// no carga ningún .env, así que cae a este fallback. El build de dev
// (`npm run build:frontend:dev`) carga frontend/.env.development con la key de dev.
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '0x4AAAAAADSbB2NTMTJBaRuN';

export function DetailsStep({
  widgetState,
  centerSlug,
  centerTimezone,
  onConfirmed,
  onSlotTaken,
  onBack,
}: DetailsStepProps) {
  const { t } = useTranslation();
  const turnstileToken = useRef<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<CustomerDetailsFormValues>({
    resolver: zodResolver(customerDetailsSchema),
  });

  const bookingMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      publicApi.createBooking(centerSlug, payload) as Promise<CreateBookingResponse>,
  });

  const onSubmit = async (values: CustomerDetailsFormValues) => {
    if (!widgetState.selectedService || !widgetState.selectedSlot) return;

    const token = turnstileToken.current;
    if (!token) {
      setError('root', { message: t('widget.verifyHuman') });
      return;
    }

    clearErrors('root');

    try {
      const result = await bookingMutation.mutateAsync({
        serviceId: widgetState.selectedService.id,
        staffId: widgetState.selectedStaff?.id,
        startTime: widgetState.selectedSlot.startTime,
        customerName: values.name,
        customerEmail: values.email,
        customerPhone: values.phone,
        notes: values.notes,
        turnstileToken: token,
      });
      onConfirmed(
        result.bookingId,
        result.trackingToken,
        result.confirmationDeadline ?? null,
      );
    } catch (err) {
      // Reset Turnstile so the user can retry
      turnstileToken.current = null;
      turnstileRef.current?.reset();

      if (err instanceof ApiRequestError) {
        if (err.status === 409) {
          onSlotTaken();
        } else if (err.message === 'BOOKING_IN_PAST') {
          setError('root', { message: t('widget.slotInPast') });
        } else {
          setError('root', { message: t('widget.bookingError') });
        }
      } else {
        setError('root', { message: t('widget.bookingError') });
      }
    }
  };

  return (
    <section aria-label={t('widget.steps.details')}>
      <h2 className="mb-4 text-lg font-semibold">{t('widget.steps.details')}</h2>

      {/* Summary */}
      {widgetState.selectedService && widgetState.selectedSlot && (
        <div className="mb-6 rounded-md border bg-muted/30 p-4 text-sm">
          <p className="font-medium">{widgetState.selectedService.name}</p>
          <p className="text-muted-foreground">
            {new Date(widgetState.selectedSlot.startTime).toLocaleString('es-ES', {
              weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
              timeZone: centerTimezone,
            })}
          </p>
          {widgetState.selectedStaff && (
            <p className="text-muted-foreground">{widgetState.selectedStaff.name}</p>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit((v) => void onSubmit(v))} noValidate className="flex flex-col gap-4">
        <FormField
          id="customer-name"
          label={t('widget.yourName')}
          required
          autoComplete="name"
          error={errors.name}
          data-testid="customer-name-input"
          {...register('name')}
        />
        <FormField
          id="customer-email"
          label={t('widget.yourEmail')}
          type="email"
          required
          autoComplete="email"
          error={errors.email}
          data-testid="customer-email-input"
          {...register('email')}
        />
        <FormField
          id="customer-phone"
          label={t('widget.yourPhone')}
          type="tel"
          required
          autoComplete="tel"
          error={errors.phone}
          data-testid="customer-phone-input"
          {...register('phone')}
        />
        <FormField
          id="customer-notes"
          label={t('widget.notes')}
          error={errors.notes}
          {...register('notes')}
        />

        <Turnstile
          ref={turnstileRef}
          siteKey={TURNSTILE_SITE_KEY}
          onSuccess={(token: string) => { turnstileToken.current = token; }}
          options={{ theme: 'light' }}
        />

        {errors.root && (
          <p role="alert" className="text-sm text-destructive">{errors.root.message}</p>
        )}

        <p className="text-xs text-muted-foreground">{t('widget.privacyNotice')}</p>

        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={onBack} data-testid="details-back">
            {t('common.back')}
          </Button>
          <Button
            type="submit"
            disabled={bookingMutation.isPending}
            className="flex-1"
            data-testid="details-submit"
          >
            {bookingMutation.isPending ? t('common.loading') : t('widget.bookButton')}
          </Button>
        </div>
      </form>
    </section>
  );
}
